/**
 * ATL-1445 stage 3, P5 — the session's outbox v2: looking at it at startup, and the one drainer per
 * session organization.
 *
 * Design: `docs/technical/write-cutover-stage3-app-seam.md` §4 (the drain-to-empty rollback) and §13
 * ("Wiring (P5)"). The queue and its drainer are P3's (`outboxDrainer.ts`); this file only decides
 * WHEN a session has one and binds its production dependencies.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * WHEN A SESSION HAS A DRAINER
 *
 * Only when there is something to deliver, or a write rollback has to prove there is not:
 *   - startup found this organization's queue non-empty (`probeOutboxV2Presence`, which runs only when
 *     `shouldProbeOutboxV2AtStartup` says the queue CAN hold anything — never for an organization
 *     today), or the session pinned Supabase mode (P9; unreachable while no bundle is capable);
 *   - the watcher started a drain-to-empty rollback (`writeCutoverWatch.ts`), which needs the queue's
 *     status to know when it is empty.
 * So for every organization today no store is opened, no drainer exists, and nothing is sent.
 *
 * A drainer, once started, delivers whatever the queue holds whatever the session's write mode:
 * every entry is a MASTER write (`writeModeFor`), and backend PR B1 applies a master-header write on
 * a Firestore-master organization as well (and reverse-mirrors it). Holding them back would leave
 * the organization's books without writes the device has already acknowledged to the cashier.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * RULES KEPT FROM P3
 *
 *  - One drainer per organization per JS context (`createOutboxDrainer` refuses a second). An org
 *    switch or sign-out calls `stop()` WITHOUT awaiting it: `stop()` waits for the send in flight,
 *    bounded only by the replay timeout, and the next drainer for that organization is the one that
 *    waits (P3's `predecessorEnded`).
 *  - `send` is `typedClient.replayRequest(request, { writeMode })`, which must forward the option
 *    (it did not — fixed in `typedApiClient.ts` by this PR).
 *  - A `master-changed` pause is lifted only after the flag was re-read (`onMasterRecheck`), and at
 *    most once per `RECHECK_MS`, so a backend that answers every master write with a 409 is not a hot
 *    loop.
 */
import { typedClient } from '../API/typedApiClient'
import { createAsyncStorageAlertLedger, createOutboxAlertSink, type OutboxAlertLedger, type OutboxAlertSink } from './outboxAlerts'
import { RECHECK_MS, createOutboxDrainer, type DrainTrigger, type OutboxDrainer, type OutboxDrainerOptions } from './outboxDrainer'
import type { OutboxSlot, OutboxStore } from './outboxStore'
import { deactivateLocalView, localViewAcknowledged, localViewListingObserver } from './localView'
import { withTimeout } from '../../utils/withTimeout'
import type { OutboxV2Presence } from './writeMode'
import type { OutboxEntryV2, OutboxLastError } from './outboxEntry'
import { writeWAckDelivered, writeWAckHeld, writeWAckListingObserver } from './writeWAcks'

/**
 * The app's outbox store, LAZILY required (review 1, item 3; as `mirrorIntent.ts` does): `./store` binds
 * the platform engine (react-native `Platform`, expo-file-system / IndexedDB), and a failure to load it
 * must surface as a store that cannot be opened — which every caller here already handles (`unknown`,
 * a page, a retry) — never as a module-graph failure that takes AppStack down with it.
 */
export function openAppOutboxStore(): OutboxStore {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { openOutboxStore } = require('./store') as { openOutboxStore: () => OutboxStore }
  return openOutboxStore()
}

/** How long startup waits for the queue listing before answering `unknown` (treated as non-empty). */
export const OUTBOX_V2_PROBE_TIMEOUT_MS = 3000

export interface OutboxV2Probe {
  presence: Exclude<OutboxV2Presence, 'not-checked'>
  /** Records listed (unreadable ones included); null when the listing failed. */
  depth: number | null
}

/**
 * P6: the last successful startup listing, kept for the local view's rebuild
 * (`startSessionOutboxAfterPin` takes it), so the queue is read once at startup, not twice. Only ever
 * set by a probe — which never runs for an organization today.
 */
let probedListing: { orgFirestoreId: string; slots: readonly OutboxSlot[] } | null = null

/** The probe's listing for this organization, once (it is cleared). */
export function takeProbedOutboxListing(orgFirestoreId: string): readonly OutboxSlot[] | null {
  if (!probedListing || probedListing.orgFirestoreId !== orgFirestoreId) return null
  const { slots } = probedListing
  probedListing = null
  return slots
}

/**
 * List the organization's queue, read-only. Never rejects; a failure or a timeout is `unknown`.
 * The store is the app's shared instance and is not closed here (P2 obligation 8 is about not
 * keeping an instance across a `close()`, which nothing here calls).
 */
export async function probeOutboxV2Presence(
  orgFirestoreId: string,
  {
    openStore = openAppOutboxStore,
    timeoutMs = OUTBOX_V2_PROBE_TIMEOUT_MS,
    logger = console,
  }: { openStore?: () => OutboxStore; timeoutMs?: number; logger?: Pick<Console, 'warn'> } = {},
): Promise<OutboxV2Probe> {
  try {
    const listed = await withTimeout(
      Promise.resolve().then(() => openStore().list(orgFirestoreId)),
      timeoutMs,
      () => new Error(`the outbox v2 listing did not answer within ${timeoutMs} ms`),
    )
    probedListing = { orgFirestoreId, slots: listed }
    return { presence: listed.length > 0 ? 'non-empty' : 'empty', depth: listed.length }
  } catch (error) {
    // A listing that failed or did not answer in time is `unknown`, which blocks Firestore mode:
    // "could not look" must never read as "empty".
    logger.warn('[outboxV2] the startup look at the queue failed or timed out; treated as non-empty', { orgFirestoreId, timeoutMs, error })
    return { presence: 'unknown', depth: null }
  }
}

/** What a rollback needs from the session's drainer. */
export type SessionOutboxV2 = Pick<OutboxDrainer, 'status' | 'subscribe' | 'drain'>

export interface SessionOutboxV2Deps {
  openStore: () => OutboxStore
  send: OutboxDrainerOptions['send']
  sink: OutboxAlertSink
  alertLedger: OutboxAlertLedger
  /** Re-read the write flag after a `master-changed` pause (the watcher's `reevaluateWritePlane`). */
  onMasterRecheck?: () => void
  /**
   * P6: the server acknowledged an entry. Defaults to the app's local view (`localViewAcknowledged`)
   * and, P9, the seam's waiter for that write (`writeWAckDelivered`).
   */
  onDelivered?: (entry: OutboxEntryV2, at: number, response: unknown) => void
  /** P9: a send held an entry. Defaults to the seam's waiter for that write (`writeWAckHeld`). */
  onHeld?: (entry: OutboxEntryV2, lastError: OutboxLastError, at: number) => void
  /**
   * P6: the drainer's listings re-sync the local view. Defaults to `localViewListingObserver` and, P9,
   * the seam's waiters (`writeWAckListingObserver`: an entry another tab delivered or held).
   */
  observeListing?: (orgFirestoreId: string) => ((slots: readonly OutboxSlot[]) => void) | null
  setTimer?: (fn: () => void, ms: number) => unknown
  clearTimer?: (handle: unknown) => void
  now?: () => number
  logger?: Pick<Console, 'warn' | 'error' | 'log'>
}

/** The production send: the master write through the counting client (`beginWrite`, ATL-889). */
export function sendOutboxV2Request(
  request: Parameters<OutboxDrainerOptions['send']>[0],
  options: Parameters<OutboxDrainerOptions['send']>[1],
): Promise<unknown> {
  return typedClient.replayRequest(request, { writeMode: options.writeMode })
}

/** The app's dependencies. A function, so nothing is constructed until a drainer is needed. */
export function productionSessionOutboxV2Deps(register?: (props: Record<string, number | null>) => unknown): SessionOutboxV2Deps {
  return {
    openStore: openAppOutboxStore,
    send: sendOutboxV2Request,
    sink: createOutboxAlertSink({ register }),
    alertLedger: createAsyncStorageAlertLedger(),
  }
}

/** P6 + P9: one listing re-syncs the local view, then settles the waiters it can judge. */
function defaultListingObserver(orgFirestoreId: string): ((slots: readonly OutboxSlot[]) => void) | null {
  const view = localViewListingObserver(orgFirestoreId)
  const acks = writeWAckListingObserver(orgFirestoreId)
  if (!view && !acks) return null
  return (slots) => {
    try {
      view?.(slots)
    } finally {
      acks?.(slots)
    }
  }
}

interface ActiveSessionOutbox {
  orgFirestoreId: string
  drainer: OutboxDrainer
  recheckTimer: unknown
  clearTimer: (handle: unknown) => void
}

let active: ActiveSessionOutbox | null = null

/**
 * P9b: who wants to know when the session's drainer starts or stops (the outbox banner,
 * `components/OutboxV2Banner.js`). A drainer starts AFTER the screens mount — the pin's callback
 * starts it (`startSessionOutboxAfterPin`) — so a screen that only read `getSessionOutboxV2` once
 * would never show the banner of a session that started later.
 */
const activeListeners = new Set<() => void>()

const notifyActiveChanged = (): void => {
  for (const listener of [...activeListeners]) {
    try {
      listener()
    } catch (error) {
      // A listener that throws never changes the drainer's lifecycle.
      console.error('[outboxV2] a session-drainer listener threw', error)
    }
  }
}

/** Be told whenever the session's drainer starts or stops. Returns the unsubscribe. */
export function subscribeSessionOutboxV2(listener: () => void): () => void {
  activeListeners.add(listener)
  return () => {
    activeListeners.delete(listener)
  }
}

/**
 * The session's drainer for this organization, started on first use (and given an `app-start` pass).
 * A drainer for ANOTHER organization is stopped first (not awaited). Throws only what
 * `createOutboxDrainer` throws (`OutboxDrainerConflictError`: a drainer this module did not start).
 */
export function ensureSessionOutboxV2(orgFirestoreId: string, deps: SessionOutboxV2Deps): SessionOutboxV2 {
  if (active && active.orgFirestoreId === orgFirestoreId) return active.drainer
  // Not awaited (module docblock): it is another organization's queue, and the next drainer for
  // THAT organization is the one that waits for it (P3). `stop()` never rejects, so `void` marks a
  // deliberate fire-and-forget, not an ignored error.
  if (active) void stopSessionOutboxV2(active.orgFirestoreId)
  const setTimer = deps.setTimer ?? ((fn: () => void, ms: number) => setTimeout(fn, ms))
  const clearTimer = deps.clearTimer ?? ((h: unknown) => clearTimeout(h as ReturnType<typeof setTimeout>))
  const logger = deps.logger ?? console
  const now = deps.now ?? Date.now
  let lastResumeAt = Number.NEGATIVE_INFINITY
  const record: ActiveSessionOutbox = {
    orgFirestoreId,
    recheckTimer: null,
    clearTimer,
    drainer: createOutboxDrainer({
      orgFirestoreId,
      openStore: deps.openStore,
      send: deps.send,
      sink: deps.sink,
      alertLedger: deps.alertLedger,
      ...(deps.setTimer ? { setTimer: deps.setTimer } : {}),
      ...(deps.clearTimer ? { clearTimer: deps.clearTimer } : {}),
      ...(deps.now ? { now: deps.now } : {}),
      onDelivered: deps.onDelivered ?? ((entry, at, response) => {
        // The waiter last: the local view must hold the acknowledgement before the writer's promise
        // resolves and the screen moves on (`fromAck` may read what the view shows).
        localViewAcknowledged(orgFirestoreId, entry.entryId, at)
        writeWAckDelivered(orgFirestoreId, entry.entryId, response)
      }),
      onHeld: deps.onHeld ?? ((entry, lastError) => writeWAckHeld(orgFirestoreId, entry.entryId, lastError)),
      observeListing: () => (deps.observeListing ?? defaultListingObserver)(orgFirestoreId),
      onMasterChanged: (detail) => {
        // The drainer is paused and sends nothing until resumed. Re-read the flag (the watcher decides
        // any reload from it), then resume — at most once per RECHECK_MS.
        if (record.recheckTimer !== null) return
        const wait = Math.max(0, lastResumeAt + RECHECK_MS - now())
        logger.warn('[outboxV2] the backend reports a write-master change; re-reading the flag', { orgFirestoreId, ...detail, waitMs: wait })
        record.recheckTimer = setTimer(() => {
          record.recheckTimer = null
          if (active !== record) return
          try {
            deps.onMasterRecheck?.()
          } catch (error) {
            logger.error('[outboxV2] the write-master re-read failed', { orgFirestoreId, error })
          }
          lastResumeAt = now()
          record.drainer.resumeAfterMasterCheck()
        }, wait)
      },
    }),
  }
  active = record
  void record.drainer.drain('app-start')
  notifyActiveChanged()
  return record.drainer
}

/** The session's drainer for this organization, if one is running. */
export function getSessionOutboxV2(orgFirestoreId: string): SessionOutboxV2 | null {
  return active && active.orgFirestoreId === orgFirestoreId ? active.drainer : null
}

/** P9: the exclusive send of the session's drainer for this organization (invariant 12), if one runs. */
export function sessionOutboxV2SendExclusive(orgFirestoreId: string): OutboxDrainer['sendExclusive'] | null {
  if (!active || active.orgFirestoreId !== orgFirestoreId) return null
  const drainer = active.drainer
  return (request, options) => drainer.sendExclusive(request, options)
}

/**
 * Kick the session's drainer, if one is running (foreground, online). Never throws. It takes no
 * organization on purpose: there is at most one session drainer, and a caller's captured organization
 * can be stale (AppStack's foreground handler outlives an organization switch — review finding).
 */
export function triggerActiveSessionOutboxV2(trigger: DrainTrigger): void {
  // `drain` never rejects (P3); the `void` is for the lint rule, not a swallowed error.
  if (active) void active.drainer.drain(trigger)
}

/**
 * Stop the session's drainer for this organization (sign-out, org switch). NOT awaited by design
 * (module docblock). Returns the drainer's `stop()` promise for tests; null when none was running.
 */
export function stopSessionOutboxV2(orgFirestoreId: string): Promise<void> | null {
  if (!active || active.orgFirestoreId !== orgFirestoreId) return null
  const record = active
  active = null
  if (record.recheckTimer !== null) record.clearTimer(record.recheckTimer)
  record.recheckTimer = null
  // P6: the local view belongs to the session's queue; it goes with it (sign-out, org switch).
  deactivateLocalView(orgFirestoreId)
  const stopped = record.drainer.stop()
  notifyActiveChanged()
  return stopped
}

/** Test seam: forget the session drainer without stopping it (a new JS context). */
export function resetSessionOutboxV2ForTests(): void {
  active = null
  probedListing = null
  activeListeners.clear()
}
